import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Link as MuiLink,
  Menu,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Typography
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { FetchClientLedgerQuery, ReportFileType, useFetchClientLedgerLazyQuery } from '../../../../generated'
import { exportClientLedger } from '../../../../services'
import {
  CLIENT_LEDGER_ENTRY_TYPES,
  FILTER_DATE_FORMAT,
  GRAPHQL_QUERY_POLICY,
  QUERY_PARAMS,
  STANDARD_DATE_FORMAT
} from '../../../constants'
import { AuthContext } from '../../../context'
import { CardBox } from '../../../theme/styleComponents'
import { anchorOrigin, flexCenter, transformOrigin } from '../../../theme/styleConstants'
import { Alert } from '../../common/Alert'
import { ContentHead } from '../../common/ContentHead'
import { EmptyData } from '../../common/EmptyData'
import MatterDropdown from '../../common/MatterDropdown'
import { hasAdminAccess } from '../../../utils'
import ClientLedgerListing from './ClientLedgerListing'

type ClientLedgerMatter = NonNullable<FetchClientLedgerQuery['fetchClientLedger']['matter']>

const ALL_ENTRY_TYPES = 'all'

/**
 * The Client Ledger screen.
 *
 * Built like the General Ledger screen — filters in the URL, a table, CSV/PDF export — with one
 * deliberate difference: a matter must be selected before anything runs. The ledger is one
 * client's account with the firm, and an all-matters run is the async pr11 report's job, not a
 * synchronous screen's.
 */
const ClientLedger = () => {
  const { currentUser } = useContext(AuthContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [exporting, setExporting] = useState(false)

  const matterId = searchParams.get(QUERY_PARAMS.MATTER) || ''
  const fromParam = searchParams.get(QUERY_PARAMS.FROM) || ''
  const toParam = searchParams.get(QUERY_PARAMS.TO) || ''
  const entryTypeParam = searchParams.get(QUERY_PARAMS.ENTRY_TYPE) || ALL_ENTRY_TYPES

  // Users outside accounting may only pick matters they are on the legal team of. The API enforces
  // this too — this only keeps the dropdown from offering matters the request would be refused for.
  const isFirmWide = hasAdminAccess(currentUser)
  const matterScopeUserId = isFirmWide ? undefined : currentUser?.id

  const hasMatter = !!matterId && matterId !== QUERY_PARAMS.ALL

  // A date range needs both ends: the report filters three different date columns by row type with
  // one range, so half a range would silently be ignored by the API. Send it only when complete.
  const dateRange = useMemo(
    () => (fromParam && toParam ? { startDate: fromParam, endDate: toParam } : {}),
    [fromParam, toParam]
  )
  const entryType = entryTypeParam === ALL_ENTRY_TYPES ? undefined : entryTypeParam

  const setParam = useCallback(
    (key: QUERY_PARAMS, value: string) => {
      if (value) searchParams.set(key, value)
      else searchParams.delete(key)
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  const getFilterValues = useCallback((key: QUERY_PARAMS) => searchParams.get(key) || '', [searchParams])

  const [ledger, setLedger] = useState<{
    headers: string[]
    rows: Record<string, string>[]
    matter?: ClientLedgerMatter | null
  }>({ headers: [], rows: [] })

  const [fetchLedger, { loading }] = useFetchClientLedgerLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      setLedger({ headers: [], rows: [] })
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { headers, rows, matter } = data?.fetchClientLedger || {}
      setLedger({ headers: headers || [], rows: (rows || []) as Record<string, string>[], matter })
    }
  })

  useEffect(() => {
    if (!hasMatter) {
      setLedger({ headers: [], rows: [] })
      return
    }

    fetchLedger({ variables: { input: { matterId, ...dateRange, entryType } } })
  }, [hasMatter, matterId, dateRange, entryType, fetchLedger])

  const runExport = useCallback(
    async (type: ReportFileType) => {
      setAnchorEl(null)
      setExporting(true)
      try {
        await exportClientLedger({ matterId, from: fromParam || undefined, to: toParam || undefined, entryType, type })
      } catch (error) {
        if (error instanceof Error) Alert.error('Error: ' + error.message)
      } finally {
        setExporting(false)
      }
    },
    [matterId, fromParam, toParam, entryType]
  )

  const datePickerProps = {
    inputFormat: STANDARD_DATE_FORMAT,
    renderInput: (params: TextFieldProps) => <TextField {...params} />
  }

  const { matter } = ledger

  return (
    <CardBox marginTop={3}>
      <ContentHead
        title={'Client Ledger'}
        customButton={() => (
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            disabled={exporting || !hasMatter || loading}
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            Export
          </Button>
        )}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{ sx: { minWidth: '120px' } }}
      >
        <MenuItem onClick={() => runExport(ReportFileType.Csv)}>CSV</MenuItem>
        <MenuItem onClick={() => runExport(ReportFileType.Pdf)}>PDF</MenuItem>
      </Menu>

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mx={2} mb={3} mt={2}>
        <Box width="320px">
          <MatterDropdown
            label="Matter"
            includeClosedMatters
            excludeConsultMatters
            hideAllOption
            required
            userId={matterScopeUserId}
            setParams={setParam}
            getFilterValues={getFilterValues}
            sx={{ width: '100%' }}
          />
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            {...datePickerProps}
            label="From"
            value={fromParam ? dayjs(fromParam) : null}
            maxDate={toParam ? dayjs(toParam) : undefined}
            onChange={value => setParam(QUERY_PARAMS.FROM, value ? dayjs(value).format(FILTER_DATE_FORMAT) : '')}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            {...datePickerProps}
            label="To"
            value={toParam ? dayjs(toParam) : null}
            minDate={fromParam ? dayjs(fromParam) : undefined}
            onChange={value => setParam(QUERY_PARAMS.TO, value ? dayjs(value).format(FILTER_DATE_FORMAT) : '')}
          />
        </LocalizationProvider>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="client-ledger-entry-type">Entry Type</InputLabel>
          <Select
            labelId="client-ledger-entry-type"
            label="Entry Type"
            value={entryTypeParam}
            onChange={event =>
              setParam(
                QUERY_PARAMS.ENTRY_TYPE,
                event.target.value === ALL_ENTRY_TYPES ? '' : String(event.target.value)
              )
            }
          >
            <MenuItem value={ALL_ENTRY_TYPES}>All</MenuItem>
            {CLIENT_LEDGER_ENTRY_TYPES.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Leaving the dates empty reports the matter's entire lifecycle — say so, rather than
          letting an empty field read as an unfinished form. */}
      <Box mx={2} mb={2}>
        <Typography variant="caption" color="text.secondary">
          Leave the dates empty for the matter&apos;s entire history. Includes closed &amp; completed matters.
        </Typography>
      </Box>

      {hasMatter && matter && (
        <Box mx={2} mb={2} display="flex" flexDirection="column" gap={0.5}>
          {/* body2 is weight 500 in the theme — only the labels are meant to read bold. */}
          <Typography variant="body2" fontWeight={400}>
            <strong>Matter:</strong>{' '}
            {matter.matterNumber ? (
              <>
                <MuiLink href={`/matters/matter-detail/${matter.id}`} target="_blank" rel="noopener noreferrer">
                  (#{matter.matterNumber})
                </MuiLink>{' '}
                {matter.matterTitle || ''}
              </>
            ) : (
              '--'
            )}
          </Typography>
          <Typography variant="body2" fontWeight={400}>
            <strong>Client:</strong> {matter.clientName || '--'}
            {matter.clientNumber && (
              <>
                {' '}
                <MuiLink href={`/contacts/client/${matter.clientNumber}`} target="_blank" rel="noopener noreferrer">
                  (#{matter.clientNumber})
                </MuiLink>
              </>
            )}
          </Typography>
          <Typography variant="body2" fontWeight={400}>
            <strong>Resp 1:</strong> {matter.responsibleAttorney || '--'}
          </Typography>
        </Box>
      )}

      {!hasMatter ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 460px)' }}>
          <EmptyData title={'Select a matter to view its ledger'} />
        </Box>
      ) : !loading && ledger.rows.length === 0 ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 460px)' }}>
          <EmptyData title={'No activity for this matter'} />
        </Box>
      ) : (
        <ClientLedgerListing loading={loading} headers={ledger.headers} rows={ledger.rows} />
      )}
    </CardBox>
  )
}

export default ClientLedger
